import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Page, ImageData } from '../../../../model/FestivalData';
import { fetchPages, updatePage, deletePage } from '../../../../services/api';
import { formatDateWithoutMilliseconds } from '../../../../utils/dateUtils';
import { v4 as uuidv4 } from 'uuid';
import ImageEditor from '../ImageEditor/ImageEditor';
import { FiChevronRight, FiFileText, FiPlus } from 'react-icons/fi';
import Modal from '../../../../components/Modal';
import PageHeader from '../../../../components/PageHeader';
import './PagesEditor.css';
import { getImageUrl } from '../../../../services/imageService';
import { ImageService } from '../../../../services/imageService';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import LoadingPlaceholder from '../../../../components/LoadingPlaceholder';
import { loadTabData } from '../../../../services/tabData';

interface PagesEditorProps {
  sessionToken: string;
  festivalId: string;
  getApiImageUrl: (identifier: string) => string;
}

const PagesEditor: React.FC<PagesEditorProps> = ({ 
  sessionToken, 
  festivalId,
  getApiImageUrl,
}) => {
  const [pages, setPages] = useState<Page[]>([]);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingIdentifier, setSavingIdentifier] = useState<string | null>(null);
  const [deletingIdentifier, setDeletingIdentifier] = useState<string | null>(null);
  const [unsavedPageIds, setUnsavedPageIds] = useState<string[]>([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const categoryInputRef = useRef<HTMLDivElement>(null);
  const t = useMessages('pages');
  const common = useMessages('common');
  const { locale } = useLanguage();

  const imageService = new ImageService(sessionToken, festivalId);

  // Get unique categories from existing pages
  const existingCategories = useMemo(() => {
    const categories = new Set(pages.map(page => page.category));
    return Array.from(categories).sort();
  }, [pages]);

  // Filter categories based on input
  const filteredCategories = useMemo(() => {
    if (!categoryFilter) return existingCategories;
    return existingCategories.filter(category => 
      category.toLowerCase().includes(categoryFilter.toLowerCase())
    );
  }, [existingCategories, categoryFilter]);

  useEffect(() => {
    const loadPages = async () => {
      try {
        const data = await loadTabData(festivalId, 'pages', () => fetchPages(sessionToken, festivalId));
        setPages(data);
      } catch (error) {
        console.error('Failed to load pages:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPages();
  }, [sessionToken, festivalId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryInputRef.current && !categoryInputRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddPage = useCallback(() => {
    const newPage: Page = {
      identifier: uuidv4(),
      title: t.titlePlaceholder,
      category: '',
      html_content: '',
      created_at: formatDateWithoutMilliseconds(new Date().toISOString()),
      updated_at: formatDateWithoutMilliseconds(new Date().toISOString()),
      order: 0,
      icon: null,
      header_image: null,
    };
    
    setUnsavedPageIds(prev => [...prev, newPage.identifier]);
    setPages(prevItems => [...prevItems, newPage]);
    setEditingPage(newPage);
  }, [pages.length, t]);

  const handleSavePage = useCallback(async (updatedPage: Page) => {
    try {
      setSavingIdentifier(updatedPage.identifier);
      setSaveError(null);
      
      let pageToSave = { ...updatedPage };

      if (pageToSave.icon) {
        const uploadedIcon = await imageService.uploadImageData(pageToSave.icon);
        pageToSave = {
          ...pageToSave,
          icon: uploadedIcon
        };
      }

      if (pageToSave.header_image) {
        const uploadedHeaderImage = await imageService.uploadImageData(pageToSave.header_image);
        pageToSave = {
          ...pageToSave,
          header_image: uploadedHeaderImage
        };
      }

      await updatePage(sessionToken, festivalId, pageToSave);
      setPages(prevItems => 
        prevItems.map(item => 
          item.identifier === pageToSave.identifier ? pageToSave : item
        )
      );
      setUnsavedPageIds(prev => prev.filter(id => id !== pageToSave.identifier));
      setEditingPage(null);
    } catch (error) {
      console.error('Failed to save page:', error);
      setSaveError(error instanceof Error ? error.message : t.saveFailed);
    } finally {
      setSavingIdentifier(null);
    }
  }, [sessionToken, festivalId, imageService, t]);

  const handleRemovePage = useCallback(async (identifier: string) => {
    try {
      setDeletingIdentifier(identifier);
      await deletePage(sessionToken, festivalId, identifier);
      setPages(prevPages => prevPages.filter(page => page.identifier !== identifier));
      setEditingPage(null);
    } catch (error) {
      console.error('Failed to delete page:', error);
    } finally {
      setDeletingIdentifier(null);
    }
  }, [sessionToken, festivalId]);

  const handleEditPage = useCallback((page: Page) => {
    setEditingPage(editingPage?.identifier === page.identifier ? null : page);
  }, [editingPage]);

  const handleCancelEdit = useCallback((page: Page) => {
    setSaveError(null);
    if (unsavedPageIds.includes(page.identifier)) {
      setPages(prevItems => 
        prevItems.filter(item => item.identifier !== page.identifier)
      );
      setUnsavedPageIds(prev => prev.filter(id => id !== page.identifier));
    }
    setEditingPage(null);
  }, [unsavedPageIds]);

  const getMaxOrderForCategory = useCallback((category: string) => {
    return Math.max(
      -1, // Use -1 as base so if no pages exist, max + 1 = 0
      ...pages
        .filter(page => page.category === category)
        .map(page => page.order)
    );
  }, [pages]);

  const handleCategorySelect = (category: string) => {
    if (editingPage) {
      const newOrder = getMaxOrderForCategory(category) + 1;
      setEditingPage({
        ...editingPage,
        category,
        order: newOrder
      });
    }
    setShowCategoryDropdown(false);
    setCategoryFilter('');
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCategory = e.target.value;
    const newOrder = getMaxOrderForCategory(newCategory) + 1;
    setEditingPage({
      ...editingPage!,
      category: newCategory,
      order: newOrder
    });
    setCategoryFilter(newCategory);
    setShowCategoryDropdown(true);
  };

  const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editingPage) {
      const value = e.target.value;
      if (value === '' || /^\d+$/.test(value)) {
        setEditingPage({
          ...editingPage,
          order: value === '' ? 0 : parseInt(value, 10)
        });
      }
    }
  };

  const sortPages = (pages: Page[]) => {
    return [...pages].sort((a, b) => {
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      return a.order - b.order;
    });
  };

  const handleIconChange = useCallback((newImage: ImageData | undefined) => {
    if (editingPage) {
      setEditingPage({
        ...editingPage,
        icon: newImage || null
      });
    }
  }, [editingPage]);

  const handleHeaderImageChange = useCallback((newImage: ImageData | undefined) => {
    if (editingPage) {
      setEditingPage({
        ...editingPage,
        header_image: newImage || null
      });
    }
  }, [editingPage]);

  if (loading) {
    return (
      <section className="pages-editor">
        <PageHeader title={t.title} />
        <LoadingPlaceholder layout="rows" />
      </section>
    );
  }

  // Sorted by category, then by order: consecutive pages of a category form its section
  const sections = sortPages(pages).reduce<{ category: string; pages: Page[] }[]>((groups, page) => {
    const last = groups[groups.length - 1];
    if (last && last.category === page.category) {
      last.pages.push(page);
    } else {
      groups.push({ category: page.category, pages: [page] });
    }
    return groups;
  }, []);

  const isUnsaved = editingPage !== null && unsavedPageIds.includes(editingPage.identifier);

  return (
    <section className="pages-editor">
      <PageHeader title={t.title} count={pages.length}>
        <button type="button" onClick={handleAddPage} className="btn btn-primary">
          <FiPlus aria-hidden="true" />
          {t.add}
        </button>
      </PageHeader>
      {sections.length === 0 && <div className="empty-state">{t.empty}</div>}
      {sections.map(section => (
        <section key={section.category} className="pages-section" data-category={section.category}>
          <div className="pages-section-head">
            <h3 className={section.category ? undefined : 'untitled'}>{section.category || t.noCategory}</h3>
            <span className="pages-section-count">{section.pages.length}</span>
          </div>
          <ul className="pages-list">
            {section.pages.map(page => (
              <li key={page.identifier}>
                <button
                  type="button"
                  className="pages-row"
                  onClick={() => handleEditPage(page)}
                >
                  {page.icon ? (
                    <img src={getImageUrl(page.icon, getApiImageUrl)} alt="" className="pages-row-icon" />
                  ) : (
                    <span className="pages-row-icon">
                      <FiFileText aria-hidden="true" />
                    </span>
                  )}
                  <span className="pages-row-title">{page.title}</span>
                  <span className="pages-row-order">{t.rowOrder(page.order)}</span>
                  <span className="pages-row-date">{new Date(page.updated_at).toLocaleDateString(locale)}</span>
                  <FiChevronRight className="pages-row-chevron" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {editingPage && (
        <Modal
          key={editingPage.identifier}
          isOpen
          onClose={() => setEditingPage(null)}
          variant="sheet"
          className="pages-sheet"
        >
          <h3>{isUnsaved ? t.newPage : t.editPage}</h3>
          {saveError && (
            <div className="pages-sheet-error" role="alert">
              {saveError}
            </div>
          )}
          <div className="pages-field">
            <label htmlFor="page-title">{t.titleLabel}</label>
            <input
              id="page-title"
              type="text"
              value={editingPage.title}
              onChange={(e) => setEditingPage({ ...editingPage, title: e.target.value })}
            />
          </div>
          <div className="pages-field-row">
            <div className="pages-field">
              <label htmlFor="page-category">{t.category}</label>
              <p id="page-category-hint" className="pages-field-hint">{t.categoryHint}</p>
              <div className="pages-category" ref={categoryInputRef}>
                <input
                  id="page-category"
                  type="text"
                  aria-describedby="page-category-hint"
                  autoComplete="off"
                  value={editingPage.category}
                  onChange={handleCategoryChange}
                  onFocus={() => setShowCategoryDropdown(true)}
                />
                {showCategoryDropdown && (
                  <div className="pages-category-suggestions">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map(category => (
                        <button
                          type="button"
                          key={category}
                          className="pages-category-option"
                          onClick={() => handleCategorySelect(category)}
                        >
                          {category}
                        </button>
                      ))
                    ) : (
                      <div className="pages-category-empty">
                        {t.newCategory}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="pages-field">
              <label htmlFor="page-order">{t.order}</label>
              <p id="page-order-hint" className="pages-field-hint">{t.orderHint}</p>
              <input
                id="page-order"
                type="text"
                inputMode="numeric"
                aria-describedby="page-order-hint"
                value={editingPage.order}
                onChange={handleOrderChange}
              />
            </div>
          </div>
          <div className="pages-field">
            <label htmlFor="page-content">{t.content}</label>
            <p id="page-content-hint" className="pages-field-hint">{t.contentHint}</p>
            <textarea
              id="page-content"
              className="pages-field-code"
              aria-describedby="page-content-hint"
              spellCheck={false}
              value={editingPage.html_content}
              onChange={(e) => setEditingPage({ ...editingPage, html_content: e.target.value })}
            />
          </div>
          <div className="pages-field pages-image-field">
            <span className="pages-field-label">{t.icon}</span>
            <p className="pages-field-hint">{t.iconHint}</p>
            <ImageEditor
              currentImage={editingPage.icon || undefined}
              onChange={handleIconChange}
              getApiImageUrl={getApiImageUrl}
            />
          </div>
          <div className="pages-field pages-image-field">
            <span className="pages-field-label">{t.headerImage}</span>
            <p className="pages-field-hint">{t.headerImageHint}</p>
            <ImageEditor
              currentImage={editingPage.header_image || undefined}
              onChange={handleHeaderImageChange}
              getApiImageUrl={getApiImageUrl}
            />
          </div>
          <div className="modal-actions">
            <button
              type="button"
              onClick={() => handleSavePage(editingPage)}
              disabled={savingIdentifier === editingPage.identifier}
            >
              {savingIdentifier === editingPage.identifier ? t.saving : common.save}
            </button>
            <button type="button" onClick={() => handleCancelEdit(editingPage)}>{common.cancel}</button>
            {!isUnsaved && (
              <button
                type="button"
                className="danger"
                onClick={() => handleRemovePage(editingPage.identifier)}
                disabled={deletingIdentifier === editingPage.identifier}
              >
                {deletingIdentifier === editingPage.identifier ? t.removing : t.remove}
              </button>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
};

export default PagesEditor; 